import { useToken } from "@gluestack-style/react";
import { ButtonSelect, SelectOption } from "components/form/ButtonSelect";
import Container from "components/general/Container";
import DismissableContent from "components/general/DismissableContent";
import PopupModal from "components/general/PopupModal";
import ScrollContainer from "components/general/ScrollContainer";
import Section from "components/general/Section";
import ItemCardList from "components/item/ItemCardList";
import Pagination from "components/navigation/Pagination";
import SitesFilterList from "components/sites/SitesFilterList";
import SitesHeader from "components/sites/SitesHeader";
import { Box, Button, H3, H4, HStack, Icon, ParagraphSmall, Text, Typography, VStack } from "components/ui";
import { useLocalSearchParams } from "expo-router";
import { ChevronDown, ChevronRight, Info, ShoppingCart } from "lucide-react-native";
import React, { useEffect, useRef, useState } from "react";
import { ActivityIndicator, Pressable } from "react-native";
import { useGetCartCurrentQuery, useGetCatalogueByIdQuery, useGetOrderQuery, useGetSitesByIdQuery, usePostCartByIdSetcurrentcartMutation } from "store/abcoApi";
import { useFilterSettings } from "utils/hooks/useFilterSettings";
import { useIsMobile } from "utils/hooks/useIsMobile";
export default function Page() {
	const resolvedPrimarylight = useToken("colors", "primarylight");
	const { id } = useLocalSearchParams<{ id: string }>();
	const [selectedCatalogue, setSelectedCatalogue] = useState(-2);
	const [pageNumber, setPageNumber] = React.useState(1);
	const [searchTerm, setSearchTerm] = React.useState("");
	const dropdownRef = useRef(null);
	const [filterValues, setFilterValues] = React.useState({} as any);
	const isMobile = useIsMobile();
	const { data: currentCart } = useGetCartCurrentQuery();
	const { data, isLoading, isFetching } = useGetSitesByIdQuery({
		id: Number(id),

		// pageNumber: pageNumber,
	});

	const [setCurrentCart, { isLoading: isSetCurrentCartLoading }] = usePostCartByIdSetcurrentcartMutation();

	const [addNew, setAddNew] = useState(false);

	const { data: existingCartCollection } = useGetOrderQuery(
		{
			orderType: "cart",
			siteIdCollection: [Number(id)],
		},
		{
			// Add these options to prevent unnecessary refetches
			refetchOnMountOrArgChange: false,
			refetchOnFocus: false,
			refetchOnReconnect: false,
		},
	);

	const filteredExistingCartList = existingCartCollection?.list?.filter((item) => item.status != "Processing") ?? [];

	const { isFilterSettingsOpen, setIsFilterOpen } = useFilterSettings();
	const {
		data: catalogueData,
		isLoading: catalogueIsLoading,
		isFetching: catalogueIsFetching,
	} = useGetCatalogueByIdQuery(
		{
			id: selectedCatalogue == -1 ? -1 : Number(data?.catalogueCollection?.[selectedCatalogue]?.id),
			pageNumber: pageNumber,
			pageSize: 10,
			searchTerm: filterValues?.searchTerm || searchTerm,
			isCore: filterValues?.isCore,
			inStock: filterValues?.inStock,
		},
		{
			skip: selectedCatalogue == -2,
			refetchOnMountOrArgChange: false,
			refetchOnFocus: false,
			refetchOnReconnect: false,
		},
	);

	useEffect(() => {
		if (selectedCatalogue === -2 && data?.catalogueCollection) {
			if (data?.catalogueCollection && data.catalogueCollection.length > 0) {
				setSelectedCatalogue(0);
			} else if (!data?.catalogueCollection || data.catalogueCollection.length === 0) {
				setSelectedCatalogue(-1);
			}
		}
	}, [data?.catalogueCollection, selectedCatalogue]);

	// Reset page number to 1 when filter values change (search term, isCore, inStock)
	useEffect(() => {
		setPageNumber(1);
	}, [filterValues?.searchTerm, filterValues?.isCore, filterValues?.inStock, searchTerm]);

	// Reset page number to 1 when selected catalogue changes
	useEffect(() => {
		setPageNumber(1);
	}, [selectedCatalogue]);

	if (isFetching && isLoading) {
		return (
			<Box height={"$full"} justifyContent="center" alignItems="center" bg="transparent">
				<Box flex={1} justifyContent="center">
					<ActivityIndicator size="large" style={{ transform: [{ scaleX: 2 }, { scaleY: 2 }] }} />
				</Box>
			</Box>
		);
	}

	if (data == null) {
		return;
	}

	const handleClosePopupModal = () => {
		setAddNew(false);
	};

	let optionList = data?.catalogueCollection?.map((item, index) => ({
		id: index,
		label: item.name || "Unnamed Catalogue",
		value: index,
	}));

	optionList?.push({
		id: -1,
		label: "Previous Purchased Items",
		value: -1,
	});

	function handleFilterChange(value: any) {
		setFilterValues((prevValues: any) => ({
			...prevValues,
			...value,
		}));
	}

	var skipSwapCart = false;
	if (filteredExistingCartList.length == 1 && filteredExistingCartList[0].id == currentCart?.id) {
		skipSwapCart = true;
	}

	return (
		<ScrollContainer>
			<SitesHeader />

			<Box bg="$neutral25">
				<Container>
					{filteredExistingCartList && filteredExistingCartList.length >= 1 && !skipSwapCart && (
						<DismissableContent marginTop={12} borderWidth={2} variant="info" icon={<Info color={resolvedPrimarylight} />}>
							<Box gap={12}>
								<Box>
									<Typography.H5>{filteredExistingCartList.length == 1 ? "You already have a cart for this site" : "You have multiple carts for this site."}</Typography.H5>
								</Box>
								<Typography.ParagraphSmall color="$neutral600"> Click any cart below to switch to it:</Typography.ParagraphSmall>
								<HStack gap={12}>
									{existingCartCollection &&
										existingCartCollection?.list?.map((cart) => {
											let isCurrentCart = cart.id === currentCart?.id;
											return (
												<Button
													key={cart.id}
													variant={isCurrentCart ? "outline" : "primary-light"}
													borderRadius={8}
													justifyContent="flex-start"
													alignItems="center"
													backgroundColor={isCurrentCart ? "$neutral400" : "$primarylight"}
													borderColor={isCurrentCart ? "$neutral400" : "$primarylight"}
													disabled={isCurrentCart}
													gap={8}
													onPress={() => {
														// @ts-ignore
														setCurrentCart({ id: cart.id });
													}}
												>
													<ShoppingCart color={"white"} />
													{cart.status == "Awaiting Approval" && (
														<Typography.ParagraphSmall color="white" weight="bold">
															(Awaiting Approval)
														</Typography.ParagraphSmall>
													)}
													<Typography.ParagraphSmall color={"white"} weight="bold">
														{cart.documentNumber}
													</Typography.ParagraphSmall>
												</Button>
											);
										})}
								</HStack>
							</Box>
						</DismissableContent>
					)}

					{optionList?.length !== 0 && (
						<HStack paddingVertical={24} gap={32}>
							{isFilterSettingsOpen && (
								<Box width={"$full"} maxWidth={312}>
									<Button
										onPress={() => {
											setIsFilterOpen(!isFilterSettingsOpen);
										}}
										justifyContent="flex-start"
										alignItems="center"
										gap={8}
										marginTop={12}
									>
										<Text color="$primarylight">Hide/show filter</Text>
										<Icon as={ChevronDown} color="$primarylight" />
									</Button>
									<SitesFilterList
										category={null}
										subCategory={null}
										onChange={handleFilterChange}
										setSearchTerm={setSearchTerm}
										isCore={filterValues?.isCore}
										setIsCore={(value) => setFilterValues((prev: any) => ({ ...prev, isCore: value }))}
										isInStock={filterValues?.inStock}
										setIsInStock={(value) => setFilterValues((prev: any) => ({ ...prev, inStock: value }))}
									/>
								</Box>
							)}
							<Box flex={1}>
								{!isMobile && (
									<Box marginTop={12} style={{ position: "relative", zIndex: 2 }}>
										<HStack alignItems="center">
											{!isFilterSettingsOpen && (
												<Button
													onPress={() => {
														setIsFilterOpen(!isFilterSettingsOpen);
													}}
													justifyContent="center"
													alignItems="center"
													gap={8}
												>
													<Text color="$primarylight">Hide/show filter</Text>
													<Icon as={ChevronRight} color="$primarylight" />
												</Button>
											)}
											{optionList && (
												<ButtonSelect
													options={optionList || []}
													value={optionList?.[selectedCatalogue] ? [optionList[selectedCatalogue]] : []}
													onChange={(selectedItems: SelectOption[]) => {
														if (selectedItems && selectedItems.length > 0 && selectedItems[0]) {
															const newSelectedValue = selectedItems[0].value; // This 'value' is the index
															if (typeof newSelectedValue === "number") {
																setSelectedCatalogue(newSelectedValue);
															}
														}
													}}
													placeholder="Select a Catalogue..."
													label="Catalogue"
													customTrigger={({ selectedItems, open, ref }) => (
														<Pressable style={{ borderWidth: 0 }} ref={ref} onPress={open}>
															<HStack gap={6} alignItems="center">
																<Typography>
																	{catalogueData?.lineCollection?.metaData.totalItemCount} products available (
																	{selectedCatalogue == -1 ? "Previous Purchased Items" : data?.catalogueCollection?.[selectedCatalogue]?.name})
																</Typography>
																<ChevronDown color="black" />
															</HStack>
														</Pressable>
													)}
												/>
											)}
										</HStack>
									</Box>
								)}
								<Box style={{ position: "relative", zIndex: 1 }}>
									<ItemCardList byCategory isGridView={false} list={catalogueData?.lineCollection?.list} scrollEnabled={false} location={data.locationId} />
								</Box>
								<Box justifyContent="center" marginTop={30} alignItems="center">
									<Pagination pageCount={catalogueData?.lineCollection?.metaData?.pageCount} pageNumber={catalogueData?.lineCollection?.metaData?.pageNumber} onChangePage={setPageNumber} />
								</Box>
							</Box>
						</HStack>
					)}

					{catalogueIsFetching ||
						(catalogueIsLoading && (
							<Box height={"$full"} justifyContent="center" alignItems="center" bg="transparent">
								<Box flex={1} justifyContent="center">
									<ActivityIndicator size="large" style={{ transform: [{ scaleX: 2 }, { scaleY: 2 }] }} />
								</Box>
							</Box>
						))}
				</Container>
			</Box>
		</ScrollContainer>
	);
}
